import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  CircularProgress,
  Stack,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Link,
  Chip,
  Collapse,
  Skeleton,
  Tooltip,
  Paper,
  TablePagination,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  PlayCircle as PlayIcon,
  ExpandLess as ExpandLessIcon,
  ShowChart as ChartIcon,
  Edit as EditIcon,
  SwipeLeft as SwipeLeftIcon,
  ContentCopy as ContentCopyIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { isLocalhost } from "../../utils/isLocalhost";
import { apiService } from "../../services/api";
import { ContentDailyMetricsGrowthSection } from "../../components/content-dialog/ContentDailyMetricsGrowthSection";
import type { DailyMetricPoint } from "../ProgramAnalysis/dailyMetricsChart";
import {
  LivestreamDetailTableRowSkeleton,
  EXPLORER_PANEL_CONTENT_SX,
  LIVESTREAM_DETAIL_TABLE_MIN_WIDTH,
  LIVESTREAM_DETAIL_TITLE_WRAP_SX,
  livestreamDetailColSx,
} from "./ExplorerLoadingSkeletons";
import {
  LivestreamViewerTimelineChart,
  mergeTimelineProgramWindows,
  programWindowsAreSameFocus,
} from "./LivestreamViewerTimelineChart";
import {
  formatViewerIntegerFloor,
  LIVESTREAM_DETAIL_GC_TIME,
  LIVESTREAM_DETAIL_STALE_TIME,
  livestreamViewsTooltip,
  formatLivestreamTableViewsCount,
  formatExplorerTableDateLine,
  formatExplorerTableTimeLine,
  LivestreamDetailSortColumn,
  SortDirection,
  RIGHT_TABLE_SORT_LABEL_SX,
  LIVESTREAM_METRIC_LABELS,
  explorerPaginationLabel,
  explorerFilterControlSx,
} from "./utils";

const CHART_PAPER_SX = {
  p: 2,
  bgcolor: "background.paper",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
} as const;
export type TimelineProgramWindow = {
  id: string;
  name: string;
  title?: string;
  startedAt: string;
  endedAt?: string | null;
  viewsAtStart?: number | null;
};

export type LivestreamDetailListItem = {
  _id: string;
  livestreamId?: string;
  url?: string;
  videoId?: string;
  title?: string;
  status?: string;
  actualStartTime?: string;
  actualEndTime?: string;
  releaseTime?: string;
  totalDuration?: number;
  youtubeDuration?: number;
  peakViewers?: number;
  averageViewers?: number;
  totalViews?: number;
  /** YouTube views of the parent video. Segment slices often have no view increment of their own. */
  streamTotalViews?: number;
  viewIncrement?: number | null;
  viewsAtStart?: number | null;
  latestMetricsCollectedAt?: string;
  livestreamProgramId?: string | null;
  programName?: string | null;
  channelId?: string;
  channelName?: string;
  /** Sibling title-program segments of the same video, oldest first. */
  programWindows?: TimelineProgramWindow[];
};

function streamDocId(item: LivestreamDetailListItem): string {
  return item.livestreamId?.trim() || item._id;
}

function livestreamRowViews(
  item: LivestreamDetailListItem,
  variant: "channel" | "program",
): number {
  if (variant === "program" && item.livestreamId) {
    const streamViews = Number(item.streamTotalViews) || 0;
    if (streamViews > 0) return streamViews;
    const increment = Math.max(0, Number(item.viewIncrement) || 0);
    if (increment > 0) return increment;
    return Math.max(0, Number(item.totalViews) || 0);
  }
  return Math.max(0, Number(item.totalViews) || 0);
}

export function programPageHighlightWindows(
  item: LivestreamDetailListItem,
): TimelineProgramWindow[] | undefined {
  const focus = programWindowFromRow(item);
  const siblings = item.programWindows ?? [];
  if (focus) {
    const matched = mergeTimelineProgramWindows(
      siblings.filter((window) => programWindowsAreSameFocus(window, focus)),
      [focus],
    );
    if (matched.length) return matched;
  }
  if (siblings.length) return siblings;
  return undefined;
}

export function programPageChartWindows(
  item: LivestreamDetailListItem,
): TimelineProgramWindow[] {
  const focus = programWindowFromRow(item);
  return mergeTimelineProgramWindows(
    item.programWindows ?? [],
    focus ? [focus] : [],
  );
}

/** Same axis on channel and program pages: parent release → last program window. */
export function livestreamViewerChartSpan(
  item: Pick<
    LivestreamDetailListItem,
    "releaseTime" | "actualEndTime" | "youtubeDuration" | "totalDuration"
  >,
  windows: TimelineProgramWindow[] = [],
): {
  releaseTime?: string;
  actualEndTime?: string;
  youtubeDuration?: number;
  totalDuration?: number;
} {
  const first = windows[0];
  const last = windows[windows.length - 1];
  return {
    releaseTime: item.releaseTime || first?.startedAt,
    actualEndTime: windows.length
      ? last?.endedAt ?? undefined
      : item.actualEndTime ?? undefined,
    youtubeDuration: item.youtubeDuration,
    totalDuration: item.totalDuration,
  };
}

function programWindowFromRow(
  row: LivestreamDetailListItem,
): TimelineProgramWindow | null {
  if (!row.actualStartTime) return null;
  const id = String(row.livestreamProgramId || "").trim();
  const name = row.programName?.trim() || "";
  if (!id && !name) return null;
  const title = row.title?.trim() || "";
  const viewsAtStart = Number(row.viewsAtStart);
  return {
    id: id || name,
    name: name || id,
    startedAt: row.actualStartTime,
    endedAt: row.actualEndTime ?? null,
    ...(title ? { title } : {}),
    ...(Number.isFinite(viewsAtStart) && viewsAtStart >= 0
      ? { viewsAtStart: Math.round(viewsAtStart) }
      : {}),
  };
}

function sameProgramWindow(
  a: TimelineProgramWindow,
  b: TimelineProgramWindow,
): boolean {
  if (a.id && b.id) return a.id === b.id;
  return a.name.trim().toLowerCase() === b.name.trim().toLowerCase();
}

export function sharedLivestreamProgramWindows(
  item: LivestreamDetailListItem,
): TimelineProgramWindow[] {
  const windows = collapseAdjacentProgramWindows(
    programPageChartWindows(item),
  );
  const ids = new Set(windows.map((window) => window.id).filter(Boolean));
  return ids.size > 1 ? windows : [];
}

/** One pill / chart band per contiguous program, not per title-change slice. */
export function collapseAdjacentProgramWindows(
  windows: TimelineProgramWindow[],
): TimelineProgramWindow[] {
  const collapsed: TimelineProgramWindow[] = [];
  for (const window of windows) {
    const prev = collapsed[collapsed.length - 1];
    if (prev && sameProgramWindow(prev, window)) {
      prev.endedAt = window.endedAt;
      continue;
    }
    collapsed.push({ ...window });
  }
  return collapsed;
}

/** Video views for a channel row. Title slices often have no increment, while the parent video does. */
function channelVideoViews(members: LivestreamDetailListItem[]): number {
  const streamViews = Math.max(
    ...members.map((row) => Number(row.streamTotalViews) || 0),
  );
  if (streamViews > 0) return streamViews;
  return members.reduce((sum, row) => sum + (Number(row.totalViews) || 0), 0);
}

/** One channel-list row per video; keep every program window for the chart. */
export function groupChannelLivestreamRows(
  rows: LivestreamDetailListItem[],
): LivestreamDetailListItem[] {
  const groups = new Map<string, LivestreamDetailListItem[]>();
  const order: string[] = [];
  for (const row of rows) {
    const key = streamDocId(row);
    const list = groups.get(key);
    if (list) list.push(row);
    else {
      groups.set(key, [row]);
      order.push(key);
    }
  }

  return order.map((key) => {
    const members = groups.get(key) ?? [];
    if (members.length <= 1) {
      const only = members[0];
      if (!only) return only as LivestreamDetailListItem;
      const window = programWindowFromRow(only);
      const totalViews = channelVideoViews(members);
      const withViews =
        totalViews > 0 ? { ...only, totalViews } : only;
      return window ? { ...withViews, programWindows: [window] } : withViews;
    }

    const sorted = [...members].sort(
      (a, b) =>
        new Date(a.actualStartTime ?? 0).getTime() -
        new Date(b.actualStartTime ?? 0).getTime(),
    );
    const current =
      sorted.find((row) => row.status === "live") ?? sorted[sorted.length - 1]!;
    const open = sorted.some((row) => !row.actualEndTime);
    const lastEnd = open
      ? undefined
      : sorted[sorted.length - 1]?.actualEndTime;
    const peakViewers = Math.max(
      ...sorted.map((row) => Number(row.peakViewers) || 0),
    );
    return {
      ...current,
      actualStartTime: sorted[0]?.actualStartTime,
      actualEndTime: lastEnd,
      peakViewers,
      totalViews: channelVideoViews(sorted),
      programWindows: sorted
        .map((row) => programWindowFromRow(row))
        .filter((window): window is TimelineProgramWindow => window != null),
    };
  });
}

const TABLE_COL_COUNT = 7;
const ROWS_PER_PAGE_OPTIONS = [10, 20, 50] as const;

const livestreamDetailTableContainerSx = {
  overflowX: "auto",
  width: "100%",
} as const;

const formatDuration = (startTime?: string, endTime?: string): string => {
  if (!startTime || !endTime) return "—";
  const start = new Date(startTime);
  const end = new Date(endTime);
  const diffMs = end.getTime() - start.getTime();
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

const formatDurationFromSeconds = (totalSeconds: number): string => {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "—";
  const hours = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

function streamDurationLabel(item: LivestreamDetailListItem): string {
  if (item.youtubeDuration != null && item.youtubeDuration > 0) {
    return formatDurationFromSeconds(item.youtubeDuration);
  }
  return formatDuration(item.actualStartTime, item.actualEndTime);
}

function youtubeVideoIdFromText(value?: string): string | undefined {
  if (!value) return undefined;
  const match = value.match(
    /(?:v=|youtu\.be\/|\/live\/|\/shorts\/)([a-zA-Z0-9_-]{11})/,
  );
  return match?.[1];
}

export function livestreamRowVideoId(
  item: Pick<LivestreamDetailListItem, "videoId" | "url">,
): string {
  return item.videoId?.trim() || youtubeVideoIdFromText(item.url) || "";
}

function livestreamSearchHaystack(item: LivestreamDetailListItem): string {
  const videoId = livestreamRowVideoId(item);
  return [
    item.title,
    item.programName,
    ...(item.programWindows ?? []).map((window) => window.name),
    item.url,
    videoId,
  ]
    .filter((value): value is string => Boolean(value?.trim()))
    .join(" ")
    .toLowerCase();
}

function livestreamMatchesSearch(
  item: LivestreamDetailListItem,
  query: string,
): boolean {
  const haystack = livestreamSearchHaystack(item);
  if (haystack.includes(query)) return true;
  const queryVideoId = youtubeVideoIdFromText(query)?.toLowerCase();
  return Boolean(queryVideoId && haystack.includes(queryVideoId));
}

function streamDurationSeconds(item: LivestreamDetailListItem): number {
  if (item.youtubeDuration != null && item.youtubeDuration > 0) {
    return item.youtubeDuration;
  }
  if (item.totalDuration != null && item.totalDuration > 0) {
    return item.totalDuration;
  }
  if (item.actualStartTime && item.actualEndTime) {
    const seconds =
      (new Date(item.actualEndTime).getTime() -
        new Date(item.actualStartTime).getTime()) /
      1000;
    return Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  }
  return 0;
}

export function livestreamRowIsLive(row: { status?: string }): boolean {
  return row.status === "live";
}

export function compareLivestreamDetailRows(
  a: LivestreamDetailListItem,
  b: LivestreamDetailListItem,
  column: LivestreamDetailSortColumn,
  direction: SortDirection,
): number {
  const liveCmp = Number(livestreamRowIsLive(b)) - Number(livestreamRowIsLive(a));
  if (liveCmp !== 0) return liveCmp;
  let cmp = 0;
  switch (column) {
    case "fecha":
      cmp =
        new Date(a.actualStartTime ?? 0).getTime() -
        new Date(b.actualStartTime ?? 0).getTime();
      break;
    case "title":
      cmp = (a.title?.trim() || "Sin título").localeCompare(
        b.title?.trim() || "Sin título",
        "es",
        { sensitivity: "base" },
      );
      break;
    case "peak":
      cmp = (Number(a.peakViewers) || 0) - (Number(b.peakViewers) || 0);
      break;
    case "duration":
      cmp = streamDurationSeconds(a) - streamDurationSeconds(b);
      break;
    case "avg":
      cmp =
        (Number(a.averageViewers) || 0) - (Number(b.averageViewers) || 0);
      break;
    case "views":
      cmp = (Number(a.totalViews) || 0) - (Number(b.totalViews) || 0);
      break;
  }
  if (cmp !== 0) return direction === "asc" ? cmp : -cmp;
  return String(a._id).localeCompare(String(b._id));
}

const OUTLINED_CHIP_SX = {
  fontWeight: 500,
  height: 22,
  color: "text.primary",
  border: "2px solid",
  borderColor: "divider",
  "& .MuiChip-label": { color: "text.primary" },
} as const;

const LivestreamEpisodeGrowthDetails: React.FC<{ livestreamId: string }> = ({
  livestreamId,
}) => {
  const { t } = useTranslation("explorer");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["livestream-daily-metrics", livestreamId],
    queryFn: () => apiService.getLivestreamDailyMetrics(livestreamId),
    staleTime: LIVESTREAM_DETAIL_STALE_TIME,
    gcTime: LIVESTREAM_DETAIL_GC_TIME,
  });

  const dailyMetrics = useMemo<DailyMetricPoint[]>(() => {
    if (!data?.dailyMetrics?.length) return [];
    return data.dailyMetrics.map((metric) => ({
      collectedAt:
        typeof metric.collectedAt === "string"
          ? metric.collectedAt
          : metric.collectedAt.toISOString(),
      daysSincePublished: metric.daysSincePublished,
      publishedAt: data.publishedAt,
      views: metric.views,
      likes: metric.likes,
      comments: metric.comments,
    }));
  }, [data]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("metricsLoadError")}
      </Typography>
    );
  }

  if (!data.dailyMetrics?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("noDailyMetricsLivestream")}
      </Typography>
    );
  }

  return (
    <ContentDailyMetricsGrowthSection
      contentId={livestreamId}
      dailyMetrics={dailyMetrics}
    />
  );
};

export type LivestreamDetailRecentListProps = {
  variant: "channel" | "program";
  livestreams: LivestreamDetailListItem[];
  /** Shown in header count; defaults to livestreams.length */
  titleCount?: number;
  /** e.g. " de 10" when program filter active on channel detail */
  titleSuffix?: string;
  isLoading?: boolean;
  isFetching?: boolean;
  emptyMessage?: string;
  /** When set and livestreams empty but totalCount > 0 */
  filteredEmptyMessage?: string;
  totalCount?: number;
  isAdmin?: boolean;
  onEditProgram?: (streamId: string) => void;
};

export const LivestreamDetailRecentList: React.FC<
  LivestreamDetailRecentListProps
> = ({
  variant,
  livestreams,
  titleCount,
  titleSuffix = "",
  isLoading = false,
  isFetching = false,
  emptyMessage,
  filteredEmptyMessage,
  totalCount = 0,
  isAdmin = false,
  onEditProgram,
}) => {
  const { t } = useTranslation("explorer");
  const [searchParams] = useSearchParams();
  const focusVideoId = searchParams.get("videoId")?.trim() || "";
  const resolvedEmpty = emptyMessage ?? t("noLivestreams");
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const appliedFocusKey = useRef<string | null>(null);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const focusedRowRef = useRef<HTMLTableRowElement | null>(null);
  const [copiedLivestreamId, setCopiedLivestreamId] = useState<string | null>(
    null,
  );
  const showCopyLivestreamId = isLocalhost();
  const [sortColumn, setSortColumn] =
    useState<LivestreamDetailSortColumn>("fecha");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    ROWS_PER_PAGE_OPTIONS[0],
  );
  const [searchInput, setSearchInput] = useState("");
  const searchQuery = searchInput.trim().toLowerCase();

  const handleSort = (column: LivestreamDetailSortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  const displayLivestreams = useMemo(
    () =>
      variant === "channel"
        ? groupChannelLivestreamRows(livestreams)
        : livestreams,
    [variant, livestreams],
  );

  const filteredLivestreams = useMemo(() => {
    if (!searchQuery) return displayLivestreams;
    return displayLivestreams.filter((item) =>
      livestreamMatchesSearch(item, searchQuery),
    );
  }, [displayLivestreams, searchQuery]);

  const sortedLivestreams = useMemo(
    () =>
      [...filteredLivestreams].sort((a, b) =>
        compareLivestreamDetailRows(a, b, sortColumn, sortDirection),
      ),
    [filteredLivestreams, sortColumn, sortDirection],
  );

  const focusedRowIndex = useMemo(() => {
    if (!focusVideoId) return -1;
    return sortedLivestreams.findIndex(
      (item) => livestreamRowVideoId(item) === focusVideoId,
    );
  }, [sortedLivestreams, focusVideoId]);

  useEffect(() => {
    if (focusedRowIndex >= 0) return;
    setPage(0);
  }, [sortColumn, sortDirection, livestreams.length, searchQuery, focusedRowIndex]);

  useLayoutEffect(() => {
    if (focusedRowIndex < 0) {
      appliedFocusKey.current = null;
      return;
    }
    const row = sortedLivestreams[focusedRowIndex];
    if (!row) return;
    const docId = streamDocId(row);
    const key = `${focusVideoId}:${docId}:${rowsPerPage}:${sortColumn}:${sortDirection}`;
    if (appliedFocusKey.current === key) return;
    appliedFocusKey.current = key;
    setPage(Math.floor(focusedRowIndex / rowsPerPage));
    setExpandedRow(docId);
  }, [
    focusedRowIndex,
    focusVideoId,
    rowsPerPage,
    sortColumn,
    sortDirection,
    sortedLivestreams,
  ]);

  useEffect(() => {
    if (!focusVideoId || focusedRowIndex < 0 || isLoading) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 30;
    const scrollToEpisodes = () => {
      if (cancelled) return;
      const target = sectionRef.current ?? focusedRowRef.current;
      if (!target) {
        if (attempts++ < maxAttempts) window.setTimeout(scrollToEpisodes, 80);
        return;
      }
      target.scrollIntoView({ block: "start" });
      const top = target.getBoundingClientRect().top;
      const nearTop = top >= 0 && top < 120;
      if (!nearTop && attempts++ < maxAttempts) {
        window.setTimeout(scrollToEpisodes, 80);
      }
    };
    const timeoutId = window.setTimeout(scrollToEpisodes, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [focusVideoId, focusedRowIndex, isFetching, isLoading, page]);

  const pagedLivestreams = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedLivestreams.slice(start, start + rowsPerPage);
  }, [sortedLivestreams, page, rowsPerPage]);

  const count = searchQuery
    ? filteredLivestreams.length
    : variant === "channel"
      ? displayLivestreams.length
      : (titleCount ?? livestreams.length);
  const showSearchEmpty =
    !isLoading && Boolean(searchQuery) && filteredLivestreams.length === 0;
  const showFilteredEmpty =
    Boolean(filteredEmptyMessage) &&
    livestreams.length === 0 &&
    totalCount > 0;

  const handleToggleChart = (streamId: string) => {
    setExpandedRow((prev) => (prev === streamId ? null : streamId));
  };

  const handleCopyLivestreamId = (streamId: string) => {
    navigator.clipboard.writeText(streamId).then(() => {
      setCopiedLivestreamId(streamId);
      window.setTimeout(() => setCopiedLivestreamId(null), 2000);
    });
  };

  const emptyBodyMessage = showSearchEmpty
    ? t("noLivestreamsMatchSearch")
    : showFilteredEmpty
      ? filteredEmptyMessage
      : resolvedEmpty;

  const showEmptyRow =
    !isLoading &&
    (showSearchEmpty || showFilteredEmpty || livestreams.length === 0);

  return (
    <Box ref={sectionRef} id="explorer-livestream-episodes">
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {isLoading ? (
            <Skeleton variant="text" width={280} height={36} />
          ) : (
            <Typography variant="h2" color="text.primary">
              {t("recentLivestreams", {
                count,
                suffix: searchQuery ? "" : titleSuffix,
              })}
            </Typography>
          )}
          {isFetching && !isLoading && <CircularProgress size={16} />}
        </Stack>
        <TextField
          size="small"
          label={t("search")}
          placeholder={t("searchByLivestream")}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{
            ...explorerFilterControlSx(240),
            minWidth: { xs: "100%", sm: 240 },
            maxWidth: { sm: 320 },
            ml: { sm: "auto" },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label={t("clearSearch")}
                  edge="end"
                  onClick={() => setSearchInput("")}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </Stack>

      <TableContainer component={Paper} sx={livestreamDetailTableContainerSx}>
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            color: "text.secondary",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <SwipeLeftIcon fontSize="small" />
          <Typography variant="caption">{t("swipeForMore")}</Typography>
        </Box>
        <Table
          sx={{
            width: "100%",
            minWidth: { xs: LIVESTREAM_DETAIL_TABLE_MIN_WIDTH, md: "100%" },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                align="left"
                sx={livestreamDetailColSx("fecha")}
                sortDirection={sortColumn === "fecha" ? sortDirection : false}
              >
                <TableSortLabel
                  active={sortColumn === "fecha"}
                  direction={
                    sortColumn === "fecha" ? sortDirection : "desc"
                  }
                  onClick={() => handleSort("fecha")}
                >
                  <Tooltip
                    title={t("tooltip.livestreamStart")}
                    arrow
                    placement="top"
                  >
                    <Box component="span" sx={{ display: "inline-flex" }}>
                      Fecha
                    </Box>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="left"
                sx={livestreamDetailColSx("title")}
                sortDirection={sortColumn === "title" ? sortDirection : false}
              >
                <TableSortLabel
                  active={sortColumn === "title"}
                  direction={
                    sortColumn === "title" ? sortDirection : "desc"
                  }
                  onClick={() => handleSort("title")}
                >
                  <Tooltip
                    title={t("tooltip.livestreamName")}
                    arrow
                    placement="top"
                  >
                    <Box component="span" sx={{ display: "inline-flex" }}>
                      Título
                    </Box>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sx={livestreamDetailColSx("views")}
                sortDirection={sortColumn === "views" ? sortDirection : false}
              >
                <TableSortLabel
                  active={sortColumn === "views"}
                  direction={sortColumn === "views" ? sortDirection : "desc"}
                  onClick={() => handleSort("views")}
                  sx={RIGHT_TABLE_SORT_LABEL_SX}
                >
                  <Tooltip
                    title={t("tooltip.contentViewsPeriod")}
                    arrow
                    placement="top"
                  >
                    <Box component="span" sx={{ display: "inline-flex" }}>
                      Views
                    </Box>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sx={livestreamDetailColSx("avg")}
                sortDirection={sortColumn === "avg" ? sortDirection : false}
              >
                <TableSortLabel
                  active={sortColumn === "avg"}
                  direction={sortColumn === "avg" ? sortDirection : "desc"}
                  onClick={() => handleSort("avg")}
                  sx={RIGHT_TABLE_SORT_LABEL_SX}
                >
                  <Tooltip
                    title={t("tooltip.avgConcurrentOne")}
                    arrow
                    placement="top"
                  >
                    <Box component="span" sx={{ display: "inline-flex" }}>
                      {LIVESTREAM_METRIC_LABELS.avgEsp}
                    </Box>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sx={livestreamDetailColSx("peak")}
                sortDirection={sortColumn === "peak" ? sortDirection : false}
              >
                <TableSortLabel
                  active={sortColumn === "peak"}
                  direction={sortColumn === "peak" ? sortDirection : "desc"}
                  onClick={() => handleSort("peak")}
                  sx={RIGHT_TABLE_SORT_LABEL_SX}
                >
                  <Tooltip
                    title={t("tooltip.maxPeakOne")}
                    arrow
                    placement="top"
                  >
                    <Box component="span" sx={{ display: "inline-flex" }}>
                      Peak Max
                    </Box>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sx={livestreamDetailColSx("duration")}
                sortDirection={
                  sortColumn === "duration" ? sortDirection : false
                }
              >
                <TableSortLabel
                  active={sortColumn === "duration"}
                  direction={
                    sortColumn === "duration" ? sortDirection : "desc"
                  }
                  onClick={() => handleSort("duration")}
                  sx={RIGHT_TABLE_SORT_LABEL_SX}
                >
                  <Tooltip title={t("tooltip.livestreamDuration")} arrow placement="top">
                    <Box component="span" sx={{ display: "inline-flex" }}>
                      Duración
                    </Box>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={livestreamDetailColSx("actions")} />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: rowsPerPage }, (_, i) => (
                <LivestreamDetailTableRowSkeleton
                  key={`livestream-table-skeleton-${i}`}
                  showSubtitleChip={variant === "channel"}
                />
              ))
            ) : showEmptyRow ? (
              <TableRow>
                <TableCell
                  colSpan={TABLE_COL_COUNT}
                  align="center"
                  sx={{ py: 4, color: "text.secondary" }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {emptyBodyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              pagedLivestreams.map((livestream) => {
                const youtubeUrl = livestream.url?.trim() || undefined;
                const titleText = livestream.title?.trim() || t("noTitle");
                const docId = streamDocId(livestream);
                const isExpanded = expandedRow === docId;
                const sharedWindows =
                  variant === "program"
                    ? sharedLivestreamProgramWindows(livestream)
                    : [];
                const chartWindows =
                  variant === "program"
                    ? programPageChartWindows(livestream)
                    : livestream.programWindows ?? [];
                const chartSpan = livestreamViewerChartSpan(
                  livestream,
                  chartWindows,
                );
                const currentProgramId = String(
                  livestream.livestreamProgramId || "",
                ).trim();

                return (
                  <React.Fragment key={docId}>
                    <TableRow
                      hover
                      ref={
                        focusVideoId &&
                        livestreamRowVideoId(livestream) === focusVideoId
                          ? focusedRowRef
                          : undefined
                      }
                      data-livestream-video-id={
                        livestreamRowVideoId(livestream) || undefined
                      }
                      onClick={() => handleToggleChart(docId)}
                      sx={{
                        cursor: "pointer",
                        bgcolor: isExpanded ? "action.selected" : undefined,
                      }}
                    >
                      <TableCell align="left" sx={livestreamDetailColSx("fecha")}>
                        <Stack spacing={0.25}>
                          <Typography variant="body2" color="text.primary">
                            {formatExplorerTableDateLine(livestream.actualStartTime)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatExplorerTableTimeLine(livestream.actualStartTime)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="left" sx={livestreamDetailColSx("title")}>
                        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                          <Box sx={LIVESTREAM_DETAIL_TITLE_WRAP_SX}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              useFlexGap
                              sx={{ minWidth: 0, flexWrap: { xs: "wrap", md: "nowrap" } }}
                            >
                              <Tooltip title={titleText} arrow placement="top">
                                <Typography
                                  variant="body1"
                                  fontWeight={600}
                                  color="text.primary"
                                  noWrap
                                  sx={{
                                    display: "block",
                                    minWidth: 0,
                                    flex: { md: 1 },
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {titleText}
                                </Typography>
                              </Tooltip>
                              {livestream.status === "live" ? (
                                <Chip
                                  label={t("live")}
                                  color="error"
                                  size="small"
                                  sx={{ fontWeight: 700, height: 22, flexShrink: 0 }}
                                />
                              ) : null}
                              {livestream.status === "processing" ? (
                                <Chip
                                  label={t("ending")}
                                  color="warning"
                                  size="small"
                                  sx={{ fontWeight: 700, height: 22, flexShrink: 0 }}
                                />
                              ) : null}
                            </Stack>
                          </Box>
                          {variant === "channel" ? (
                            <Stack
                              direction="row"
                              spacing={0.5}
                              useFlexGap
                              flexWrap="wrap"
                              sx={{ alignSelf: "flex-start" }}
                            >
                              {(livestream.programWindows?.length
                                ? collapseAdjacentProgramWindows(
                                    livestream.programWindows,
                                  )
                                : [
                                    {
                                      id: livestream.livestreamProgramId || livestream._id,
                                      name: livestream.programName?.trim() || "",
                                      startedAt: livestream.actualStartTime || "",
                                      endedAt: livestream.actualEndTime,
                                    },
                                  ]
                              ).map((window, index) => (
                                <Chip
                                  key={`${window.id}-${window.startedAt}-${index}`}
                                  size="small"
                                  variant="outlined"
                                  label={
                                    window.name ||
                                    (livestream.livestreamProgramId?.trim()
                                      ? t("program")
                                      : t("noProgram"))
                                  }
                                  sx={OUTLINED_CHIP_SX}
                                />
                              ))}
                            </Stack>
                          ) : null}
                          {sharedWindows.length > 0 ? (
                            <Stack
                              direction="row"
                              spacing={0.5}
                              useFlexGap
                              flexWrap="wrap"
                              alignItems="center"
                              sx={{ alignSelf: "flex-start", minWidth: 0 }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ lineHeight: 1.2, flexShrink: 0 }}
                              >
                                {t("sharedLivestream")}
                              </Typography>
                              {sharedWindows.map((window, index) => (
                                <Chip
                                  key={`${window.id}-${window.startedAt}-${index}`}
                                  size="small"
                                  variant="outlined"
                                  label={
                                    window.name ||
                                    (window.id ? t("program") : t("noProgram"))
                                  }
                                  sx={{
                                    ...OUTLINED_CHIP_SX,
                                    fontWeight:
                                      currentProgramId &&
                                      window.id === currentProgramId
                                        ? 700
                                        : 500,
                                  }}
                                />
                              ))}
                            </Stack>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={livestreamDetailColSx("views")}>
                        <Tooltip
                          title={livestreamViewsTooltip(
                            livestream.latestMetricsCollectedAt,
                            livestreamRowViews(livestream, variant),
                          )}
                          arrow
                          placement="top"
                        >
                          <Typography
                            variant="body2"
                            color="text.primary"
                            component="span"
                            noWrap
                            sx={{
                              cursor: "default",
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {formatLivestreamTableViewsCount(
                              livestreamRowViews(livestream, variant),
                            )}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={livestreamDetailColSx("avg")}>
                        <Typography variant="body2" color="text.primary">
                          {formatViewerIntegerFloor(livestream.averageViewers || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={livestreamDetailColSx("peak")}>
                        <Typography variant="body2" color="text.primary">
                          {formatViewerIntegerFloor(livestream.peakViewers || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={livestreamDetailColSx("duration")}>
                        <Typography variant="body2" color="text.primary">
                          {streamDurationLabel(livestream)}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={livestreamDetailColSx("actions")}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="flex-end"
                          spacing={0.5}
                          sx={{ flexShrink: 0 }}
                        >
                          {isAdmin && onEditProgram ? (
                            <Tooltip title={t("editAssignedProgram")}>
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => onEditProgram(docId)}
                                aria-label={t("editProgram")}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {showCopyLivestreamId ? (
                            <Tooltip
                              title={
                                copiedLivestreamId === docId
                                  ? t("copied")
                                  : t("copyLivestreamId")
                              }
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleCopyLivestreamId(docId)}
                                aria-label={t("copyLivestreamId")}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {youtubeUrl ? (
                            <Link
                              href={youtubeUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <IconButton size="small" sx={{ color: "error.main" }}>
                                <PlayIcon />
                              </IconButton>
                            </Link>
                          ) : null}
                          <Tooltip
                            title={
                              isExpanded
                                ? t("hideChart")
                                : t("showViewerChart")
                            }
                          >
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleToggleChart(docId)}
                              aria-label={t("showViewerChart")}
                            >
                              {isExpanded ? <ExpandLessIcon /> : <ChartIcon />}
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={TABLE_COL_COUNT}
                        sx={{ py: 0, px: 0, borderBottom: isExpanded ? undefined : 0 }}
                      >
                        <Collapse in={isExpanded} unmountOnExit>
                          <Box sx={{ ...EXPLORER_PANEL_CONTENT_SX, py: 2 }}>
                            <Stack spacing={2}>
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: "0.9rem",
                                    fontWeight: 700,
                                    color: "text.primary",
                                    letterSpacing: "0.05em",
                                    mb: 1,
                                  }}
                                >
                                  {t("liveViewers")}
                                </Typography>
                                <Paper elevation={0} sx={CHART_PAPER_SX}>
                                  <LivestreamViewerTimelineChart
                                    livestreamId={streamDocId(livestream)}
                                    title={livestream.title}
                                    releaseTime={chartSpan.releaseTime}
                                    actualEndTime={chartSpan.actualEndTime}
                                    youtubeDuration={chartSpan.youtubeDuration}
                                    totalDuration={chartSpan.totalDuration}
                                    programWindows={chartWindows}
                                    highlightOnly={variant === "program"}
                                    highlightWindows={
                                      variant === "program"
                                        ? programPageHighlightWindows(livestream)
                                        : undefined
                                    }
                                  />
                                </Paper>
                              </Box>
                              <LivestreamEpisodeGrowthDetails
                                livestreamId={streamDocId(livestream)}
                              />
                            </Stack>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
        {!isLoading && sortedLivestreams.length > 0 ? (
          <TablePagination
            component="div"
            count={sortedLivestreams.length}
            page={Math.min(
              page,
              Math.max(0, Math.ceil(sortedLivestreams.length / rowsPerPage) - 1),
            )}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[...ROWS_PER_PAGE_OPTIONS]}
            labelRowsPerPage={t("rowsPerPage")}
            labelDisplayedRows={({ from, to, count }) =>
              explorerPaginationLabel(from, to, count)
            }
          />
        ) : null}
      </TableContainer>
    </Box>
  );
};
